import React from "react";
import { CheckSquare, Clock, Book } from "lucide-react";
import { motion } from "framer-motion";

const AssignmentTable = ({
  assignments = [],
  onAssignmentClick,
  getAssignmentStatus,
  getStatusStyle,
  getStatusLabel,
  formatDate,
}) => {
  if (assignments.length === 0) {
    return (
      <div className="border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-sm p-8 text-center">
        <CheckSquare className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
          No Assignments Yet
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
          Assignments will appear here when they are created.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      {/* Desktop Table */}
      <table className="w-full hidden md:table">
        <thead>
          <tr className="bg-gray-100 dark:bg-gray-900">
            <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Assignment
            </th>
            <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Course
            </th>
            <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Due Date
            </th>
            <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Status
            </th>
          </tr>
        </thead>
        <tbody>
          {assignments.map((assignment, index) => {
            const status = getAssignmentStatus(assignment);
            return (
              <motion.tr
                key={assignment._id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.1 + index * 0.08 }}
                className="group border-b border-gray-100 dark:border-gray-800 border-l-2 border-l-transparent hover:border-l-accent1 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-all"
                onClick={() => onAssignmentClick?.(assignment)}
              >
                <td className="py-3 px-4">
                  <div className="flex items-center">
                    <div className="h-8 w-8 rounded-sm bg-accent1/10 flex items-center justify-center text-accent1 mr-3">
                      <CheckSquare className="h-4 w-4" />
                    </div>
                    <div className="flex flex-col">
                      <span className="font-bold text-sm text-gray-900 dark:text-white">
                        {assignment.title}
                      </span>
                      {assignment.description && (
                        <span className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1 mt-0.5">
                          {assignment.description}
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td className="py-3 px-4 text-sm text-gray-500 dark:text-gray-400">
                  {assignment.courseName}
                </td>
                <td className="py-3 px-4">
                  <div className="flex items-center">
                    <Clock className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 mr-1.5" />
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      {assignment.dueDate
                        ? formatDate(assignment.dueDate)
                        : "No due date"}
                    </span>
                  </div>
                </td>
                <td className="py-3 px-4">
                  <span
                    className={`px-2.5 py-1 rounded-sm text-xs font-medium border ${getStatusStyle(status)}`}
                  >
                    {getStatusLabel(status)}
                  </span>
                </td>
              </motion.tr>
            );
          })}
        </tbody>
      </table>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {assignments.map((assignment, index) => {
          const status = getAssignmentStatus(assignment);
          return (
            <motion.div
              key={assignment._id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1 + index * 0.08 }}
              className="bg-white dark:bg-gray-800 rounded-sm border border-gray-200 dark:border-gray-700 shadow-sm dark:shadow-none border-l-2 border-l-transparent hover:border-l-accent1 p-4 cursor-pointer transition-all"
              onClick={() => onAssignmentClick?.(assignment)}
            >
              <div className="flex items-start space-x-3 mb-3">
                <div className="h-10 w-10 rounded-sm bg-accent1/10 flex items-center justify-center text-accent1 flex-shrink-0">
                  <CheckSquare className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-sm text-gray-900 dark:text-white mb-1">
                    {assignment.title}
                  </h3>
                  {assignment.description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                      {assignment.description}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-col space-y-2 text-sm">
                <div className="flex items-center text-gray-600 dark:text-gray-300">
                  <Book className="h-3.5 w-3.5 mr-1.5 text-gray-400 dark:text-gray-500" />
                  <span className="truncate">{assignment.courseName}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-gray-600 dark:text-gray-300">
                    <Clock className="h-3.5 w-3.5 mr-1.5 text-gray-400 dark:text-gray-500" />
                    <span className="text-xs">
                      {assignment.dueDate
                        ? formatDate(assignment.dueDate)
                        : "No due date"}
                    </span>
                  </div>
                  <span
                    className={`px-2.5 py-1 rounded-sm text-xs font-medium border ${getStatusStyle(status)}`}
                  >
                    {getStatusLabel(status)}
                  </span>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default AssignmentTable;
